// 고객 「내 현장」 에 무엇을 보일지 — 저장소 밖 한 벌(인메모리·Supabase 공통), 내부 열은 여기서 떨군다.

import type { ChangeOrder, ChangeOrderStatus } from "../change-order/types";
import type { Contract, ContractShare, ContractShareView } from "../contract/rows";
import { contractSharePath, liveShareOf } from "../contract/view";
import { phaseNameOf } from "./activity";
import type {
  Project,
  ProjectLog,
  ProjectLogKind,
  ProjectPhase,
  ProjectPhaseStatus,
  ProjectPhoto,
  ProjectStatus,
} from "./types";

// 저장소가 토큰으로 모아 오는 원본 — 화면에는 그대로 넘기지 않고 `toCustomerProjectView` 를 거친다.
export interface ProjectShareView {
  project: Project;
  phases: ProjectPhase[];
  logs: ProjectLog[];
  photos: ProjectPhoto[];
  phase_defs: { code: string; name: string }[];
  assignee_name: string | null;
}

// 허용 목록이다 — 새 구분이 생겨도 여기 넣기 전까지는 고객에게 안 보인다(노쇼는 내부 사정이라 뺀다).
export const CUSTOMER_VISIBLE_LOG_KINDS: readonly ProjectLogKind[] = ["NOTE", "ISSUE", "EXTRA_WORK", "WARRANTY_AS"];

export const isCustomerVisibleLog = (kind: ProjectLogKind): boolean => CUSTOMER_VISIBLE_LOG_KINDS.includes(kind);

export interface CustomerPhase {
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: ProjectPhaseStatus;
}

export interface CustomerLog {
  id: string;
  log_date: string;
  kind: ProjectLogKind;
  body: string;
  weather?: string | null;
  // 일지에 단 공정 이름 — 공정 없이 쓴 일지는 null.
  phase_name?: string | null;
}

export interface CustomerPhoto {
  id: string;
  log_date: string;
  caption: string;
  phase_name: string | null;
}

export interface CustomerDay {
  date: string;
  logs: CustomerLog[];
  photos: CustomerPhoto[];
}

export interface CustomerProjectView {
  title: string;
  status: ProjectStatus;
  planned_start_date: string | null;
  planned_end_date: string | null;
  started_at: string | null;
  completion_date: string | null;
  warranty_end_date: string | null;
  assignee_name: string | null;
  phases: CustomerPhase[];
  days: CustomerDay[];
  photos: CustomerPhoto[];
}

const byDateDesc = (a: string, b: string): number => (a < b ? 1 : a > b ? -1 : 0);

// 원본 → 고객 화면 값. 필드를 하나씩 골라 담는다(펼쳐 담으면 새 내부 열이 조용히 따라 나간다).
export const toCustomerProjectView = (view: ProjectShareView): CustomerProjectView => {
  const { project } = view;
  const nameOf = (code: string) => phaseNameOf(view.phase_defs, code);
  const phaseName = new Map(view.phases.map((p) => [p.id, nameOf(p.phase_code)]));

  const logs: CustomerLog[] = view.logs
    .filter((l) => isCustomerVisibleLog(l.kind))
    .map((l) => ({
      id: l.id,
      log_date: l.log_date,
      kind: l.kind,
      body: l.body,
      weather: l.weather ?? null,
      phase_name: l.project_phase_id === null ? null : phaseName.get(l.project_phase_id) ?? null,
    }));
  const photos: CustomerPhoto[] = view.photos
    .map((p) => ({
      id: p.id,
      log_date: p.log_date,
      caption: p.caption,
      phase_name: p.project_phase_id === null ? null : phaseName.get(p.project_phase_id) ?? null,
    }))
    .sort((a, b) => byDateDesc(a.log_date, b.log_date));

  const dates = [...new Set([...logs.map((l) => l.log_date), ...photos.map((p) => p.log_date)])].sort(byDateDesc);
  const days = dates.map((date) => ({
    date,
    logs: logs.filter((l) => l.log_date === date),
    photos: photos.filter((p) => p.log_date === date),
  }));

  return {
    title: project.title,
    status: project.status,
    planned_start_date: project.planned_start_date,
    planned_end_date: project.planned_end_date,
    started_at: project.started_at,
    completion_date: project.completion_date,
    warranty_end_date: project.warranty_end_date,
    assignee_name: view.assignee_name,
    phases: [...view.phases]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => ({ name: nameOf(p.phase_code), start_date: p.start_date, end_date: p.end_date, status: p.status })),
    days,
    photos,
  };
};

// 고객 일지 한 건 — 「내 현장」 과 같은 규칙으로 고른 일지와 그날 사진(보이지 않는 일지면 null).
export const toCustomerLogDetail = (view: ProjectShareView, logId: string): { log: CustomerLog; photos: CustomerPhoto[] } | null => {
  for (const day of toCustomerProjectView(view).days) {
    const log = day.logs.find((l) => l.id === logId);
    if (log) return { log, photos: day.photos };
  }
  return null;
};

// 고객 이름·현장 주소 — 계약서 링크가 보이는 체결본 값이 기준, 스냅샷이 없을 때만 계약 행.
export const customerPlaceOf = (view: {
  snapshot: Pick<NonNullable<ContractShareView["snapshot"]>, "data"> | null;
  contract: Pick<Contract, "client_name" | "site_address">;
}): { client_name: string; site_address: string } => ({
  client_name: view.snapshot?.data.client.name ?? view.contract.client_name,
  site_address: view.snapshot?.data.site_address ?? view.contract.site_address,
});

// 고객 「내 현장」 주소 — 계약 링크 아래에 둬 입장 쿠키(path=/c/<token>)를 같이 탄다.
export const customerProjectPath = (token: string): string => `${contractSharePath(token)}/project`;

export const customerPhotoPath = (token: string, photoId: string): string =>
  `${customerProjectPath(token)}/photos/${encodeURIComponent(photoId)}`;

// 고객 일지 한 건(/c/<token>/log/<logId>) — 화면 링크·알림 링크가 같은 한 벌.
export const customerLogPath = (token: string, logId: string): string =>
  `${contractSharePath(token)}/log/${encodeURIComponent(logId)}`;

// 고객 A/S 접수·목록(/c/<token>/as).
export const customerAsPath = (token: string): string => `${contractSharePath(token)}/as`;

// 고객 우리 집 기록(/c/<token>/house).
export const customerHousePath = (token: string): string => `${contractSharePath(token)}/house`;

// A/S 사진 바이트는 api 가 입장 쿠키로 내준다(화면 주소가 아니라 /api 주소).
export const customerAsPhotoPath = (token: string, asId: string, index: number): string =>
  `/api/c/${encodeURIComponent(token)}/as/${encodeURIComponent(asId)}/photos/${index}`;

export interface CustomerChangeOrder {
  seq: number;
  title: string;
  delta_total: number;
  status: ChangeOrderStatus;
}

// 변경공사는 회차·제목·증감액·상태만(제목은 계약서 링크가 이미 보인다) — 문서·결정 기록은 계약서 화면(합의 폼)에서 본다.
export const toCustomerChangeOrders = (list: readonly Pick<ChangeOrder, "seq" | "title" | "delta_total" | "status">[]): CustomerChangeOrder[] =>
  [...list]
    .sort((a, b) => a.seq - b.seq)
    .map((co) => ({ seq: co.seq, title: co.title, delta_total: co.delta_total, status: co.status }));

export const CUSTOMER_LINK_CONTRACT_REASON = "체결·준공된 계약이 아니라 고객 보기 링크를 열 수 없습니다";
export const CUSTOMER_LINK_SHARE_REASON = "살아 있는 계약 링크가 없어 고객 보기 링크를 만들 수 없습니다";

// 담당자 「고객 보기 링크 복사」 — 고객 화면과 같은 조건(체결·준공 + 살아 있는 계약 링크)일 때만 토큰을 준다.
export const customerLinkTokenOf = (
  contractStatus: Contract["status"] | null,
  shares: readonly ContractShare[],
  at: string
): { token: string; reason: null } | { token: null; reason: string } => {
  if (contractStatus !== "SIGNED" && contractStatus !== "COMPLETED") return { token: null, reason: CUSTOMER_LINK_CONTRACT_REASON };
  const live = liveShareOf(shares, at);
  return live ? { token: live.token, reason: null } : { token: null, reason: CUSTOMER_LINK_SHARE_REASON };
};
